'use client';
import Link from 'next/link';
import { styled, css } from 'styled-components';
import { NavLink, StyledHeaderButton } from '../styles';

export const StyledNavigationBar = styled.nav`
  background-color: ${props =>
    props.$fullBleedNav
      ? 'transparent'
      : props.theme.colors.get(props.$bgColor)};
  color: ${props => props.theme.colors.get(props.$fontColor)};
  width: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(5, 0)};
  ${props => props.theme.tablet} {
    padding: ${props =>
      props.showLogo &&
      props.theme.spacing(props.$verticalPadding, props.$horizontalPadding)};
  }
  @media ${props => props.theme.laptopL} {
    max-width: 1472px;
    margin: auto;
  }
`;

export const SkipToContent = styled.a`
  position: fixed;
  left: 0;
  color: ${props => props.theme.colors.text.primary};
  transform: translateX(-110%);
  transition: transform 0.3s;
  padding: 2px 5px;

  &:focus {
    transform: translateY(0%);
  }
`;

export const OllieLogo = styled.div`
  display: flex;
  z-index: 10;

  @media ${props => props.theme.mobileS} {
    flex: 1;
    justify-content: left;
  }

  @media ${props => props.theme.mobileL} {
    flex: ${props => (props.$hasCenterLinks ? 'none' : 1)};
    justify-content: 'left';
  }
`;

export const LogoImgContainer = styled.div`
  max-height: 48px;
  width: auto;

  & img {
    max-height: 100%;
    width: auto;
  }
  height: 48px;
  z-index: 10;
`;

export const LeftAligned = styled.div`
  display: flex;
  justify-content: left;
  @media ${props => props.theme.mobileS} {
    margin-right: ${({ theme }) => theme.spacing(3)};
  }
  @media ${props => props.theme.tablet} {
    margin-right: ${({ theme }) => theme.spacing(0)};
  }
`;

export const CenterAligned = styled.div`
  display: flex;
  justify-content: center;

  @media ${props => props.theme.mobileS} {
    flex: none;
  }

  @media ${props => props.theme.mobileL} {
    flex: ${props => (props.$hasCenterLinks ? 1 : 'none')};
  }
`;

export const RightAligned = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export const NavigationButton = styled.div`
  display: none;
  @media ${props => props.theme.mobileS} {
  &:first-child {
  display:block;
  }
    a {
      button {
        min-height: 56px;
      }
    }
  }
@media ${props => props.theme.laptop} {
  display: block;
`;

export const MobileCtaButton = styled.div`
  display: block;
`;

export const NavigationMenuLink = styled.li`
  ${props => props.theme.typography.heading3};
  display: flex;
  list-style: none;
  text-align: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  & button {
    margin: 0;
  }
`;

export const DesktopNavigationMenu = styled.menu`
  display: none;
  padding: unset;
  @media (min-width: 1200px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${props => props.theme.spacing(7)};
    padding-top: ${props => props.theme.spacing(2)};

    ${NavLink}, ${StyledHeaderButton} {
      position: relative;
      display: inline-block;
      font-weight: 700;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background-color: ${props => props.theme.colors.secondary.lilac};
        transform: scaleX(0);
        transform-origin: left center;
        transition: transform 250ms ease-in-out;
      }

      &:hover,
      &:focus-visible {
        color: ${props => props.theme.colors.secondary.lilac};
      }

      &:hover::after,
      &:focus-visible::after {
        transform: scaleX(1);
      }
    }

    ${StyledHeaderButton} {
      &:hover,
      &:focus-visible {
        border-bottom: none;
      }
    }
  }
`;

export const MobileNavigationMenu = styled.div`
  display: flex;
  ${props => props.theme.typography.heading3};
  @media (min-width: 1200px) {
    display: none;
  }
        ${NavLink}, ${StyledHeaderButton} {
          position: relative;
          display: inline-block;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background-color: ${props => props.theme.colors.secondary.lilac};
        transform: scaleX(0);
        transform-origin: left center;
        transition: transform 250ms ease-in-out;
      }

      &:hover,
      &:focus-visible {
        color: ${props => props.theme.colors.secondary.lilac};
      }

      &:hover::after,
      &:focus-visible::after {
        transform: scaleX(1);
      }
    }

    ${StyledHeaderButton} {
      &:hover,
      &:focus-visible {
        border-bottom: none;
      }
    }
  }
`;

export const MobileCtaButtonContainer = styled.div`
  width: 100vw;
  max-width: 765px;
  flex-shrink: 0;
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 1;

  ${props =>
    props.$buttonCount > 1 &&
    css`
      padding: ${props => props.theme.spacing(3)};
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      gap: ${props => props.theme.spacing(3)};
      background-color: ${props =>
        props.$bgColor
          ? props.theme.colors.get(props.$bgColor)
          : props.theme.colors.background.base};
    `};
`;

export const MobileCtaMemberButtonContainer = styled.div`
  width: 100%;
`;

export const MobileButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${props => props.theme.spacing(props.verticalMargin, 0)};
`;

export const BurgerMenu = styled.button`
  background: none;
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(1)};
  border: 2px solid transparent;
  &:focus,
  &:active {
    border: 2px solid ${props => props.theme.colors.border.dark};
  }
`;

export const SlideOutMenu = styled.menu`
  color: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  letter-spacing: 0.01em;
  padding: ${props => props.theme.spacing(4, 0)};
  width: 100%;
`;

export const MobileDrawerScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: ${props =>
    props.theme.getContrastColors(props.$fillColor ?? 'background.dark')}.color;
`;

export const SlideOutMenuTopSection = styled.menu`
  flex-shrink: 0;
  width: 100%;
  ${props =>
    props.$hideLoginLink
      ? css`
          display: flex;
          flex-direction: ${props =>
            props.closeButtonPosition === 'left' ? 'row' : 'row-reverse'};
          padding: ${props => props.theme.spacing(6, 0)};
        `
      : css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-gap: 40px;
          align-items: center;
          padding: ${props => props.theme.spacing(6)};
        `};
  justify-content: space-between;
`;

export const SlideOutMenuCloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props =>
    props.theme.colors.get(
      props.$backgroundColor,
      props.theme.colors.shadowGrey,
    )};
  ${props =>
    props.$borderColor &&
    css`
      border: 2px solid
        ${props.theme.colors.get(
          props.$borderColor,
          props.theme.colors.shadowGrey,
        )};
    `}
  border-radius: 50%;
  width: 50px;
  max-height: 50px;
  padding: ${props => props.theme.spacing(3)};
`;

export const LogoLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid transparent;
  &:focus,
  &:active {
    border: 2px solid ${props => props.theme.colors.border.dark};
    border-radius: 16px;
  }
`;

export const StyledStepCounter = styled.div`
  ${props => props.theme.typography.body};
  color: ${props => props.theme.colors.text.secondary};
`;
